import { useState } from 'react';
import { amountSchema, type EstimateDetail, type UpdateEstimateInput } from 'used-vehicle-contracts';
import { DEFAULT_OVERRIDABLE_KEYS } from 'used-vehicle-engine';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DefinitionList } from '../../components/ui/DefinitionList';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { formatMoney } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { PercentInput } from '../templates/PercentInput';
import { toOptionalNumber } from './estimates-display';
import type { LocalValuation } from './valuation-local';

type FactsPatch = Omit<UpdateEstimateInput, 'estimateId' | 'expectedRevision'>;

export interface CommercialTargetsProps {
  detail: EstimateDetail;
  local: { valuation: LocalValuation } | null;
  /** What the run priced the profit at: the fixed target, or the margin applied to the appraisal. */
  profitAllowance: number | null;
  riskBuffer: number | null;
  disabled: boolean;
  onSave: (save: FactsPatch) => void;
}

/** A margin is edited as a percentage and stored as a fraction (plan §11.6), below the catalogue's 90%. */
const MAX_MARGIN_PERCENT = 90;

const round = (value: number, decimals: number): number => Math.round(value * 10 ** decimals) / 10 ** decimals;

/**
 * "Profit CTA wants to make" and "Safety margin", editable on the estimate (business review 2026-09-23,
 * D5; plan §11.19).
 *
 * Both are template values an estimate may override — `targetProfit` (or `targetMargin` when the estimate
 * prices on a margin) and `riskBuffer` are in the default overridable set — so the edit is a
 * `paramOverrides` save: material to the fingerprint, so an approval granted before it goes stale, and
 * bounded by the catalogue on the service, so a negative figure is refused there as well as here. A key
 * the template does not allow is shown, not offered.
 */
export const CommercialTargets = ({ detail, local, profitAllowance, riskBuffer, disabled, onSave }: CommercialTargetsProps) => {
  const { t } = useI18nTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [profit, setProfit] = useState('');
  const [risk, setRisk] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isMargin = detail.profitMode === 'MARGIN';
  const profitKey = isMargin ? 'targetMargin' : 'targetProfit';
  // The engine reads a version that does not narrow the list as the default set; an estimate with no
  // template has no shared values to override at all (plan §11 fix 10).
  const allowed = new Set(detail.template === null ? [] : (local?.valuation.bundle.template.overridableKeys ?? DEFAULT_OVERRIDABLE_KEYS));
  const canEditProfit = allowed.has(profitKey);
  const canEditRisk = allowed.has('riskBuffer');
  const canEdit = !disabled && (canEditProfit || canEditRisk);

  const profitOverride = detail.paramOverrides[profitKey]?.value;
  const riskOverride = detail.paramOverrides.riskBuffer?.value;
  const inputs = local?.valuation.built.inputs ?? {};

  const open = (): void => {
    const currentProfit = inputs[profitKey];

    setProfit(currentProfit === undefined ? '' : String(isMargin ? round(currentProfit * 100, 4) : currentProfit));
    setRisk(riskBuffer === null ? '' : String(riskBuffer));
    setError(null);
    setIsEditing(true);
  };

  const save = (): void => {
    const profitValue = toOptionalNumber(profit);
    const riskValue = toOptionalNumber(risk);
    const profitValid =
      profitValue === null || (isMargin ? profitValue >= 0 && profitValue <= MAX_MARGIN_PERCENT : amountSchema.safeParse(profitValue).success);

    if (!profitValid || (riskValue !== null && !amountSchema.safeParse(riskValue).success)) {
      setError(t('estimates.targetsInvalid'));

      return;
    }

    const overrides: NonNullable<FactsPatch['paramOverrides']> = {};

    if (canEditProfit) {
      overrides[profitKey] = { value: profitValue === null ? null : isMargin ? round(profitValue / 100, 6) : profitValue };
    }

    if (canEditRisk) {
      overrides.riskBuffer = { value: riskValue };
    }

    onSave({ paramOverrides: overrides });
    setIsEditing(false);
  };

  const restoreTemplateValues = (): void => {
    const overrides: NonNullable<FactsPatch['paramOverrides']> = {};

    if (canEditProfit) {
      overrides[profitKey] = { value: null };
    }

    if (canEditRisk) {
      overrides.riskBuffer = { value: null };
    }

    onSave({ paramOverrides: overrides });
    setIsEditing(false);
  };

  const changedBadge = <Badge tone="info">{t('estimates.changedForEstimate')}</Badge>;

  return (
    <div className="mt-2 rounded-md border border-dashed border-border p-3">
      <DefinitionList
        items={[
          {
            key: 'profit',
            label: t('estimates.whyProfitAllowance'),
            value: (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span
                  dir="ltr"
                  className="tabular-nums"
                >
                  {formatMoney(profitAllowance, { exact: true })}
                </span>
                {isMargin && inputs.targetMargin !== undefined ? (
                  <span className="text-xs text-muted-foreground">
                    {t('estimates.marginOfAppraisal', { percent: round(inputs.targetMargin * 100, 2) })}
                  </span>
                ) : null}
                {profitOverride === undefined ? null : changedBadge}
              </span>
            ),
          },
          {
            key: 'risk',
            label: t('estimates.whyRiskBuffer'),
            value: (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span
                  dir="ltr"
                  className="tabular-nums"
                >
                  {formatMoney(riskBuffer, { exact: true })}
                </span>
                {riskOverride === undefined ? null : changedBadge}
              </span>
            ),
          },
        ]}
      />

      {isEditing ? (
        <div className="mt-3 space-y-3">
          {canEditProfit ? (
            <FormField
              id="estimate-target-profit"
              label={isMargin ? t('estimates.targetMarginPercent') : t('estimates.targetProfitJod')}
            >
              {isMargin ? (
                <PercentInput
                  id="estimate-target-profit"
                  value={profit}
                  onValueChange={setProfit}
                />
              ) : (
                <Input
                  id="estimate-target-profit"
                  type="number"
                  dir="ltr"
                  min={0}
                  step="any"
                  className="tabular-nums"
                  value={profit}
                  onChange={(event) => setProfit(event.target.value)}
                />
              )}
            </FormField>
          ) : null}
          {canEditRisk ? (
            <FormField
              id="estimate-risk-buffer"
              label={t('estimates.riskBufferJod')}
            >
              <Input
                id="estimate-risk-buffer"
                type="number"
                dir="ltr"
                min={0}
                step="any"
                className="tabular-nums"
                value={risk}
                onChange={(event) => setRisk(event.target.value)}
              />
            </FormField>
          ) : null}
          {error === null ? null : (
            <p
              role="alert"
              className="text-xs text-destructive"
            >
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={save}
            >
              {t('actions.save')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(false)}
            >
              {t('actions.cancel')}
            </Button>
          </div>
        </div>
      ) : canEdit ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={open}
          >
            {t('estimates.editTargets')}
          </Button>
          {profitOverride === undefined && riskOverride === undefined ? null : (
            <Button
              size="sm"
              variant="ghost"
              onClick={restoreTemplateValues}
            >
              {t('estimates.useTemplateTargets')}
            </Button>
          )}
          <span className="text-xs text-muted-foreground">{t('estimates.editTargetsHint')}</span>
        </div>
      ) : null}
    </div>
  );
};
